import { RaRecord } from 'react-admin';

export type JsonApiLink =
  | null
  | string
  | {
      href: string;
      rel?: string;
      describedby?: Record<string, JsonApiLink>;
      title?: string;
      type?: string;
      hreflang?: string | string[];
      meta?: Record<string, unknown>;
    };

export type JsonApiMeta = Record<string, unknown>;

export interface JsonApiResourceLinkage<Type extends string> {
  type: Type;
  id: string;
}

export function isJsonApiResourceLinkage<Type extends string>(
  value: unknown,
): value is JsonApiResourceLinkage<Type> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    typeof value.type === 'string' &&
    'id' in value &&
    typeof value.id === 'string'
  );
}

export interface JsonApiHasOneRelationship<Type extends string> {
  links?: {
    self: JsonApiLink;
    related: JsonApiLink;
  };
  data?: JsonApiResourceLinkage<Type> | null;
  meta?: JsonApiMeta;
}

export function isJsonApiHasOneRelationship<Type extends string>(
  value: unknown,
): value is JsonApiHasOneRelationship<Type> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    (value.data === null || isJsonApiResourceLinkage(value.data))
  );
}

export interface JsonApiHasManyRelationship<Type extends string> {
  links?: {
    self: JsonApiLink;
    related: JsonApiLink;
  };
  data: JsonApiResourceLinkage<Type>[];
  meta?: JsonApiMeta;
}

export function isJsonApiHasManyRelationship<Type extends string>(
  value: unknown,
): value is JsonApiHasManyRelationship<Type> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray(value.data) &&
    value.data.every(isJsonApiResourceLinkage)
  );
}

export type JsonApiResource<
  Type extends string = string,
  Attributes extends Record<string, unknown> = Record<string, unknown>,
  Relationships extends Record<
    string,
    JsonApiHasOneRelationship<string> | JsonApiHasManyRelationship<string>
  > = Record<string, JsonApiHasOneRelationship<string> | JsonApiHasManyRelationship<string>>,
> = RaRecord<string> & {
  attributes: Attributes;
  links?: Record<string, JsonApiLink>;
  meta?: JsonApiMeta;
  relationships: Relationships;
  type: Type;
};

export interface JsonApiError {
  id?: string;
  links?: Record<string, JsonApiLink>;
  status?: '400' | '401' | '403' | '404' | '409' | '422' | '500' | '503';
  code?: string;
  title?: string;
  detail?: string;
  source?: {
    pointer?: string;
    parameter?: string;
    header?: string;
  };
  meta?: JsonApiMeta;
}

export interface JsonApiErrorResponse {
  errors: JsonApiError[];
  status?: number;
  meta?: JsonApiMeta;
}

export interface ReactAdminError {
  errors: ReactAdminValidationErrors;
  apiResponse: JsonApiErrorResponse;
}

export interface JsonApiSingleResponse<
  Type extends string = string,
  Entity extends Record<string, unknown> = Record<string, unknown>,
> {
  data?: JsonApiResource<Type, Entity>;
  errors?: JsonApiError[];
  included?: JsonApiResource[];
  links?: Record<string, JsonApiLink>;
  meta?: JsonApiMeta;
}

export interface JsonApiCollectionResponse<
  Type extends string = string,
  Entity extends Record<string, unknown> = Record<string, unknown>,
> {
  data: JsonApiResource<Type, Entity>[];
  errors?: JsonApiError[];
  included?: JsonApiResource[];
  links?: Record<string, JsonApiLink>;
  meta?: JsonApiMeta;
}

export interface JsonApiBulkOperationsResponse {
  'atomic:results': {
    data?: JsonApiResource;
    errors?: JsonApiError[];
    included?: JsonApiResource[];
    links?: Record<string, JsonApiLink>;
    meta?: JsonApiMeta;
  }[];
  meta?: JsonApiMeta;
}

/**
 * Type-safe error handling for React-Admin server-side validation.
 *
 * React-Admin expects validation errors to be structured with an 'errors' object
 * containing field-specific error messages and an optional 'root.serverError' for
 * global errors. The standard HttpError class uses 'any' for the body property,
 * making it difficult to work with validation errors in a type-safe way.
 *
 * This interface and TypedHttpError class provide proper TypeScript typing
 * for React-Admin's validation error format, ensuring type safety when
 * throwing and handling server-side validation errors.
 *
 * @see https://marmelab.com/react-admin/Validation.html#server-side-validation
 */
export interface ReactAdminValidationErrors {
  errors: {
    root?: {
      serverError: string;
    };
  } & Record<
    string,
    | string
    | {
        message: string;
        args?: Record<string, unknown>;
      }
  >;
}

type JsonApiErrorLike = Partial<Pick<Error, 'message'>> & {
  body?: JsonApiErrorResponse;
  status?: number;
};

function asJsonApiError(error: unknown): JsonApiErrorLike | undefined {
  return error == null ? undefined : (error as JsonApiErrorLike);
}

export function getJsonApiErrorBody(error: unknown): JsonApiErrorResponse | undefined {
  return asJsonApiError(error)?.body;
}

export function getJsonApiErrorMessage(error: unknown, fallback: string): string {
  const jsonApiError = asJsonApiError(error);

  return jsonApiError?.body?.errors?.[0]?.detail ?? jsonApiError?.message ?? fallback;
}

export function getJsonApiErrorStatus(error: unknown): number {
  const jsonApiError = asJsonApiError(error);

  return jsonApiError?.body?.status ?? jsonApiError?.status ?? 500;
}

export function createReactAdminValidationError(error: unknown): ReactAdminValidationErrors {
  return {
    errors: {
      root: {
        serverError: getJsonApiErrorMessage(error, 'Server error'),
      },
    } as ReactAdminValidationErrors['errors'],
  };
}
